<template>
  <div
    class="cart relative"
    :class="{'pseudo-element': !fullWidth}"
    :style="{
      '--custom-page-background-color': customPageBackgroundColor,
    }"
  >
    <div :class="{container: !fullWidth}">
      <div class="row">
        <div class="primary-col col-md-8">
          <cart-messages
            v-if="hasMessages"
            :messages="messages"
            @dismiss="clearMessages()"
          />

          <slot name="heading" :title="basketTitle">
            <h1>{{ basketTitle }}</h1>
          </slot>

          <slot
            name="subheading"
            :items="items"
            :waiting-for-items="waitingForItems"
            :requires-shipping="requiresShipping"
            :free-shipping-text="freeShippingText"
            :free-shipping-icon="freeShippingIcon"
          >
            <p
              v-if="!empty && requiresShipping && freeShippingText"
              class="mb-3"
            >
              <font-awesome-icon
                class="icon text-spacing"
                :icon="freeShippingIcon"
              />
              {{ freeShippingText }}
            </p>
            <p
              v-if="
                theme().get('checkout.shipping.showEstimates') === '1' &&
                  maxDispatchDays > 0
              "
              class="mb-4"
            >
              <font-awesome-icon :icon="faTruck" class="mr-2" />Order ships out
              in {{ maxDispatchDays }} days
            </p>
          </slot>

          <tml-loader :loading="waitingForItems" height="2em">
            <cart-item-form
              v-for="item in items"
              :key="item.key + item.quantity"
              :item="item"
              :allow-change-quantity="!item.fixedQuantity"
              :allow-options-change="item.context === 'free-item'"
              :allow-remove="!item.preview"
              :use-quantity-select="settings.useQuantitySelect"
              :show-discount="settings.showItemDiscount"
              :dispatch-days="
                getItemDispatchDays(
                  item,
                  item.options[0]?.['Colour'],
                  item.options[0]?.['Size']
                )
              "
              :stock-config-loading="stockConfigLoading"
              :include-tax="!settings.showTax"
              @remove-item="removeItem"
              @update-item="updateItem"
            />
          </tml-loader>

          <div v-if="!mobileView" class="mt-24 mr-4">
            <slot name="content-under" />
          </div>
        </div>

        <div
          v-if="!mobileView || !waitingForItems"
          class="secondary-col col-md-4"
        >
          <div ref="checkoutArea">
            <slot
              name="right-sidebar-heading"
              :title="settings.rightSidebarTitle"
            >
              <h2>{{ settings.rightSidebarTitle }}</h2>
            </slot>

            <cart-coupon-form
              v-if="settings.allowCouponCodes"
              :code="coupon"
              :message="couponMessage"
              @apply-coupon="applyCoupon"
            />

            <cart-totals
              :summary="summary"
              :show-tax="settings.showTax"
              :show-shipping="
                !freepostDisabled && summary.total > freeShippingMinimum
              "
            />

            <div v-if="!empty" class="checkout">
              <template v-if="summary.total > 0">
                <cart-express-checkout
                  v-if="paymentAccountTypes.includes('stripe')"
                  class="mb-2"
                  :default-total="summary.total"
                  :name="name"
                  :route-prefix="routePrefix"
                  @closed="onClose"
                  @click="sendInitiateCheckoutEvent"
                  @rendered="stripeButtonRendered = true"
                />
                <cart-paypal-button
                  v-if="paymentAccountTypes.includes('paypal')"
                  class="mb-4"
                  :total="summary.total"
                  :shipping="summary.shipping"
                  :discount="summary.discount"
                  :show-tax="settings.showTax"
                  :name="name"
                  :route-prefix="routePrefix"
                  :items="items"
                  @closed="onClose"
                  @click="sendInitiateCheckoutEvent"
                  @basket-changed="getBasket"
                  @invalid-coupon="invalidCoupon"
                />
                <div
                  v-if="
                    (!failedToLoadStripe && stripeButtonRendered) ||
                      paymentAccountTypes.includes('paypal')
                  "
                  class="text-line-break mb-4"
                >
                  <span> or </span>
                </div>
              </template>

              <stripe-error-notice v-if="failedToLoadStripe" />
              <!-- hardcoded border radius to match Stripe and Paypal buttons. Can be themed when we implement new Stripe element, which can be styled. -->
              <tml-button
                v-else
                primary
                class="w-full mb-6"
                href="/checkout/"
                border-radius="4px"
                :text="checkoutButtonText"
              />
            </div>
          </div>
          <slot name="right-sidebar-bottom" />
        </div>
      </div>
    </div>
    <slot name="content-under" />
  </div>
</template>

<script>
import {computed} from 'vue';
import {useHead} from '@vueuse/head';
import {
  faCheck,
  faShippingFast,
  faPartyHorn,
  faTruckFast,
  faTruck,
} from '@fortawesome/pro-light-svg-icons';
import {tracker} from '@teemill/common/plugins';
import {failedToLoadStripe} from '@teemill/common/services';
import {StoreMixin} from '@teemill/components/mixins';
import {detectCountry, formatUrl, viewportSize} from '@teemill/common/helpers';
import {http, useGtm} from '@teemill/common/services';
import {currency, usePaymentProcessorStore} from '@teemill/common/vuex';
import {isEUCountryCode, addAlpha} from '@teemill/utilities';
import cartCheckoutMixins from './cart-checkout-mixins';
import CartItemForm from './CartItemForm.vue';
import CartCouponForm from './CartCouponForm.vue';
import CartTotals from './CartTotals.vue';
import CartMessages from './cart/CartMessages.vue';
import CartPaypalButton from './CartPaypalButton.vue';
import CartExpressCheckout from './CartExpressCheckout.vue';
import StripeErrorNotice from './common/StripeErrorNotice.vue';

export default {
  name: 'TmlCart',

  components: {
    CartItemForm,
    CartCouponForm,
    CartTotals,
    CartMessages,
    CartPaypalButton,
    CartExpressCheckout,
    StripeErrorNotice,
  },

  mixins: [StoreMixin, cartCheckoutMixins],

  inject: ['app', 'theme'],

  props: {
    importedSettings: {
      type: Object,
      required: false,
    },

    name: {
      type: String,
      required: true,
    },

    routePrefix: {
      type: String,
      default: '/omnis/v3/checkout/frontend/',
    },

    fullWidth: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      faCheck,
      faShippingFast,
      faPartyHorn,
      faTruckFast,
      faTruck,

      waitingForItems: true,
      // Timeout is the delay between the quantity field being changed and when the axios request is sent.
      timeout: null,
      settings: {
        allowCouponCodes: true,
        title: 'Your Bag',
        rightSidebarTitle: 'Checkout Now',
        showItemDiscount: false,
        emptySuffix: 'is empty',
        showTax: false,
        useQuantitySelect: true,
      },
      stripeButtonRendered: false,
      failedToLoadStripe,
    };
  },

  computed: {
    paymentAccountTypes() {
      if (this.app.isTeemill) {
        return ['stripe', 'paypal'];
      }

      return usePaymentProcessorStore().getter('paymentAccountTypes') || [];
    },

    usesNewProductFeed() {
      return this.$store.state.subdomain.usesNewProductFeed;
    },

    basketTitle() {
      const title = this.settings.title;

      if (this.empty && !this.waitingForItems) {
        return `${title} ${this.settings.emptySuffix}`;
      }

      return title;
    },

    currency() {
      return this.$store.getters['currency/get']();
    },

    freeShippingText() {
      const locationCountry = detectCountry();

      if (
        this.freepostDisabled ||
        (locationCountry && locationCountry !== 'GB') ||
        (this.currency && this.currency.code !== 'GBP')
      ) {
        return;
      }

      const min = this.freeShippingThresholdUK;

      return this.isFreepost
        ? 'Order qualifies for Free UK Shipping'
        : `Orders over £${min} qualify for Free UK Shipping`;
    },

    // Are there any items?
    empty() {
      if (this.items?.length === 0) {
        return true;
      }

      return false;
    },

    qualifiesForFreeShipping() {
      return (
        this.country !== 'US' && this.summary.total >= this.freeShippingMinimum
      );
    },

    freeShippingIcon() {
      if (this.qualifiesForFreeShipping) {
        return faCheck;
      }

      return faShippingFast;
    },

    isRapanui: () => $store.state.subdomain?.division === 14,

    checkoutButtonText() {
      return this.summary.total > 0 ? 'Pay by card' : 'Checkout';
    },

    mobileView() {
      return viewportSize.isSmaller('md');
    },

    freeShippingMinimum() {
      let min;

      switch (true) {
        case this.country === 'GB':
          min = this.freeShippingThresholdUK;
          break;

        case isEUCountryCode(this.country) &&
          this.freeShippingThresholdEU !== null:
          min = this.freeShippingThresholdEU;
          break;
      }

      return min;
    },

    customPageBackgroundColor() {
      return addAlpha(this.theme().get('text.color'), 0.05);
    },

    maxDispatchDays() {
      return this.getMaxDispatchDays();
    },
  },

  created() {
    useHead(
      computed(() => ({
        title: $store.state.title,
      }))
    );

    this.initialiseBasket();
  },

  mounted() {
    tracker.event('view-basket');

    this.getBasket().then(() => {
      this.sendViewCartEvent();
    });
  },

  methods: {
    viewportSize,

    /**
     * Sets the default settings for the Basket
     */
    initialiseBasket() {
      const importedSettings = this.importedSettings;

      if (importedSettings) {
        this.settings = Object.assign(this.settings, importedSettings);
      }
    },

    onClose() {
      if (this.waitingForItems) {
        return;
      }

      this.getBasket();
    },

    sendViewCartEvent() {
      if (!this.app.isTeemill) {
        useGtm().viewCart({
          items: this.items.map(item => ({
            id: this.usesNewProductFeed ? item.productUuid : item.productId,
            name: item.name,
            brand: JSON.stringify(this.$store.state.subdomain.company.name),
            price: item.priceIncTax,
            quantity: item.quantity,
            currency: this.$store?.state?.subdomain?.currency ?? 'GBP',
            options: item.options.map(option => ({
              color: option.Colour,
              size: option.Size,
            })),
          })),
          value: this.summary.subtotal,
          currency: this.$store?.state?.subdomain?.currency ?? 'GBP',
        });
      }
    },

    /**
     * Removes an item from the basket
     */
    removeItem(key, resolve, reject) {
      this.$store
        .dispatch('cart/removeItem', {
          key,
          name: this.name,
        })
        .then(data => {
          if (data) {
            this.handleCartResource(data);
          }

          if (data.freeShippingThresholds) {
            this.freeShippingThresholdUK = data.freeShippingThresholds.uk;
            this.freeShippingThresholdEU =
              data.freeShippingThresholds.eu ?? null;
          }

          resolve();
        })
        .catch(() => {
          this.handleFatalError();
          reject();
        });
    },

    /**
     * Updates an item in the basket
     */
    updateItem(key, itemData, resolve, reject) {
      this.$store
        .dispatch('cart/updateItem', {
          key,
          giftWrapOptionId: itemData.giftWrapOptionId,
          quantity: itemData.quantity,
          colour: itemData.colour,
          size: itemData.size,
          name: this.name,
        })
        .then(data => {
          if (data) {
            this.handleCartResource(data);
          }

          if (data.freeShippingThresholds) {
            this.freeShippingThresholdUK = data.freeShippingThresholds.uk;
            this.freeShippingThresholdEU =
              data.freeShippingThresholds.eu ?? null;
          }

          resolve(this.items.find(i => i.key === key));
        })
        .catch(() => {
          this.handleFatalError();
          reject();
        });
    },

    handleFatalError() {
      snackbar.error(`
        Sorry, something went wrong, please refresh the page or try
        again later.
      `);
    },

    invalidCoupon() {
      this.coupon = null;
      this.couponMessage = null;

      this.getBasket();
    },
  },
};
</script>

<style lang="scss" scoped>
@media (max-width: 767px) {
  .cart {
    .container {
      max-width: 100%;
    }
  }
}

.pseudo-element {
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    z-index: 0;
    background-color: var(--custom-page-background-color);

    // matches viewport & container sizes in tailwind.config.js
    @media (max-width: theme('screens.sm')) {
      display: none;
    }
    @media (min-width: theme('screens.md')) {
      width: calc(((100% - 720px) / 2));
    }

    @media (min-width: theme('screens.lg')) {
      width: calc(((100% - 960px) / 2));
    }

    @media (min-width: theme('screens.xl')) {
      width: calc(((100% - 1140px) / 2));
    }
  }
}

.primary-col {
  background-color: var(--tml-page-background-color);

  @media (min-width: 768px) {
    min-height: calc(100vh - #{$header-height});
  }
}

.secondary-col {
  background-color: var(--custom-page-background-color);
}

.text-line-break {
  display: block;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;

  span {
    position: relative;
    display: inline-block;

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      width: 9999px;
      height: 1px;
      background-color: var(--tml-border-color);
    }

    &:before {
      right: 100%;
      margin-right: 15px;
    }

    &:after {
      left: 100%;
      margin-left: 15px;
    }
  }
}
</style>
